import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, X } from 'lucide-react';
import { chatSocket, CHAT_EVENTS } from '@services/chatSocket';
import { useAuthStore } from '@store/authStore';
import { useChatNotificationStore } from '@store/chatNotificationStore';
import { initMessageSoundUnlock, playMessageSound } from '@utils/messageSound';
import { ensureNotificationPermission, showDesktopNotification } from '@utils/desktopNotification';
import type { Message } from '@/types';

interface ToastItem {
  id: string;
  message: Message;
  partnerName?: string;
}

interface GlobalChatNotificationsProps {
  messagesBasePath: '/wholesaler/messages' | '/restaurant/messages';
}

export function GlobalChatNotifications({ messagesBasePath }: GlobalChatNotificationsProps) {
  const navigate = useNavigate();
  const userId = useAuthStore((s) => s.user?.id);
  const activePartnerId = useChatNotificationStore((s) => s.activePartnerId);
  const activePartnerIdRef = useRef(activePartnerId);
  activePartnerIdRef.current = activePartnerId;
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const seenIdsRef = useRef(new Set<string>());
  const originalTitleRef = useRef(typeof document !== 'undefined' ? document.title : '');
  const titleFlashRef = useRef<number | null>(null);

  useEffect(() => {
    const cleanupUnlock = initMessageSoundUnlock();
    void ensureNotificationPermission();
    return cleanupUnlock;
  }, []);

  useEffect(() => {
    if (!userId) return;

    const off = chatSocket.on(CHAT_EVENTS.MESSAGE_NEW, (payload) => {
      const data = payload as { message: Message };
      const msg = data.message;
      if (msg.receiverId !== userId) return;
      if (seenIdsRef.current.has(msg.id)) return;
      seenIdsRef.current.add(msg.id);

      const partnerId = msg.senderId;
      const isViewingPartnerInForeground =
        partnerId === activePartnerIdRef.current && document.visibilityState === 'visible';

      // Mesaj zaten açık sohbette görünüyorsa bildirim/ses gereksiz.
      if (isViewingPartnerInForeground) return;

      const partnerName = msg.sender?.name ?? 'Yeni mesaj';
      const openChat = () => navigate(`${messagesBasePath}/${partnerId}`);

      void playMessageSound();

      if (document.visibilityState === 'hidden') {
        showDesktopNotification(partnerName, {
          body: msg.content,
          tag: `msg-${msg.id}`,
          onClick: openChat,
        });
        flashDocumentTitle(`💬 ${partnerName}`);
      } else {
        setToasts((prev) => [
          ...prev.slice(-2),
          { id: msg.id, message: msg, partnerName },
        ]);
      }
    });

    return off;
  }, [messagesBasePath, navigate, userId]);

  useEffect(() => {
    if (!toasts.length) return;
    const timer = window.setTimeout(() => {
      setToasts((prev) => prev.slice(1));
    }, 8000);
    return () => window.clearTimeout(timer);
  }, [toasts]);

  useEffect(
    () => () => {
      if (titleFlashRef.current) {
        window.clearInterval(titleFlashRef.current);
        document.title = originalTitleRef.current;
      }
    },
    [],
  );

  function flashDocumentTitle(alertTitle: string) {
    if (titleFlashRef.current) window.clearInterval(titleFlashRef.current);
    let showAlert = true;
    titleFlashRef.current = window.setInterval(() => {
      document.title = showAlert ? alertTitle : originalTitleRef.current;
      showAlert = !showAlert;
    }, 1200);

    const stop = () => {
      if (titleFlashRef.current) {
        window.clearInterval(titleFlashRef.current);
        titleFlashRef.current = null;
      }
      document.title = originalTitleRef.current;
      document.removeEventListener('visibilitychange', stop);
    };
    document.addEventListener('visibilitychange', stop);
  }

  if (!toasts.length) return null;

  return (
    <div
      className="fixed bottom-24 right-4 z-[100] flex w-full max-w-sm flex-col gap-2 lg:bottom-6"
      role="region"
      aria-live="polite"
      aria-label="Mesaj bildirimleri"
    >
      {toasts.map((toast) => (
        <button
          key={toast.id}
          type="button"
          onClick={() => {
            navigate(`${messagesBasePath}/${toast.message.senderId}`);
            setToasts((prev) => prev.filter((t) => t.id !== toast.id));
          }}
          className="flex items-start gap-3 rounded-xl border-2 border-brand-400 bg-white p-4 text-left shadow-2xl ring-2 ring-brand-200/60 transition hover:border-brand-500"
        >
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-100 text-brand-600">
            <MessageCircle className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-slate-900">{toast.partnerName ?? 'Yeni mesaj'}</p>
            <p className="mt-1 line-clamp-2 text-sm text-slate-600">{toast.message.content}</p>
            <p className="mt-2 text-xs font-medium text-brand-600">Yanıtlamak için tıklayın</p>
          </div>
          <X
            className="h-4 w-4 shrink-0 text-slate-400"
            onClick={(e) => {
              e.stopPropagation();
              setToasts((prev) => prev.filter((t) => t.id !== toast.id));
            }}
          />
        </button>
      ))}
    </div>
  );
}
